import type { AnalyzeGardenResult } from "@orchestration/runtime/use-cases/analyze/analyze-garden";
import { CHARTJS_UMD_SOURCE } from "./chartjs-vendor.generated";
import { reportClientScript } from "./report-client-script";
import { reportStyles } from "./report-styles";

/**
 * Renders a self-contained HTML metrics report for a garden's analyze result: a fixed-height
 * chart section (Chart.js, vendored inline — no CDN/network) pinned at the top, and a flat,
 * always-expanded, independently-scrolling step table beneath it. Data is inlined as JSON; the
 * whole document has no external references and works fully offline.
 */
export function renderGardenReport(result: AnalyzeGardenResult): string {
  const dataJson = JSON.stringify(result).replace(/</g, "\\u003c");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Workflower report: ${escapeHtml(result.gardenName)}</title>
<style>${reportStyles()}</style>
</head>
<body>
<div class="viz-root">
  <header class="summary-strip">
    <h1>${escapeHtml(result.gardenName)}</h1>
    <span class="summary-item"><span class="label">Total time</span><span class="value" id="summaryDuration"></span></span>
    <span class="summary-item"><span class="label">Total tokens</span><span class="value" id="summaryTokens"></span></span>
    <span class="summary-item"><span class="label">Total cost</span><span class="value" id="summaryCost"></span></span>
    <span class="summary-item"><span class="label">Errors</span><span class="value" id="summaryErrors"></span></span>
  </header>

  <section class="chart-section">
    <div class="chart-controls">
      <div class="toggle-group" data-role="view-mode">
        <button type="button" data-value="steps" class="active">Steps</button>
        <button type="button" data-value="workflows">Workflows</button>
      </div>
      <div class="toggle-group" data-role="metric">
        <button type="button" data-value="time" class="active">Time</button>
        <button type="button" data-value="tokens">Tokens</button>
        <button type="button" data-value="price">Price</button>
        <button type="button" data-value="thinking">Thinking</button>
      </div>
    </div>
    <div class="chart-canvas-wrap">
      <canvas id="gardenChart"></canvas>
    </div>
  </section>

  <section class="table-section">
    <div class="table-scroll">
      <table id="stepsTable">
        <thead>
          <tr>
            <th data-sort-key="globalOrder">#</th>
            <th>Flower</th>
            <th>Step</th>
            <th>Model</th>
            <th data-sort-key="thinkingLevel">Thinking</th>
            <th data-sort-key="durationMs">Duration</th>
            <th data-sort-key="usage.input">Input tok</th>
            <th data-sort-key="usage.output">Output tok</th>
            <th data-sort-key="usage.cacheRead">Cache read tok</th>
            <th data-sort-key="usage.cacheWrite">Cache write tok</th>
            <th data-sort-key="usage.totalTokens">Total tok</th>
            <th data-sort-key="usage.cost.input">Input $</th>
            <th data-sort-key="usage.cost.output">Output $</th>
            <th data-sort-key="usage.cost.cacheRead">Cache read $</th>
            <th data-sort-key="usage.cost.cacheWrite">Cache write $</th>
            <th data-sort-key="usage.cost.total">Total $</th>
            <th data-sort-key="toolCallCount">Tool calls</th>
            <th>Tools used</th>
            <th data-sort-key="errorCount">Errors</th>
            <th>Last error</th>
          </tr>
        </thead>
        <tbody id="stepsTableBody"></tbody>
      </table>
    </div>
  </section>
</div>

<script>${CHARTJS_UMD_SOURCE}</script>
<script>window.__GARDEN_REPORT_DATA__ = ${dataJson};</script>
<script>${reportClientScript()}</script>
</body>
</html>
`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}
